import { t } from '@glory/i18n';
import { FadeOnChange, TRANSITION } from '@glory/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useId } from 'react';

import { useCrumbs } from '../../lib/crumbs.tsx';
import { orpc } from '../../lib/orpc.ts';
import { usePermissions } from '../../lib/permissions.ts';
import { meQuery } from '../../lib/session.ts';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsLayout,
});

/** Разделы настроек по группам; без `permission` — видны всем. Личное — первым (карта взаимодействия, 4.3). */
const GROUPS = [
  {
    label: t('settings.groupPersonal'),
    sections: [{ to: '/settings/notifications', label: t('settings.notifications') }],
  },
  {
    label: t('settings.groupStudio'),
    sections: [
      { to: '/settings/studio', label: t('settings.studio'), permission: 'studio.manage' },
      { to: '/settings/roles', label: t('access.ui.page.nav'), permission: 'roles.assign' },
      { to: '/settings/specializations', label: t('settings.specializations') },
      { to: '/settings/calendar', label: t('settings.calendar') },
      { to: '/settings/sales', label: t('settings.sales'), permission: 'sales.manage' },
      { to: '/settings/proposals', label: t('settings.proposals'), permission: 'proposals.manage' },
      { to: '/settings/production', label: t('projects.settingsPage.title'), permission: 'projects.manage' },
      { to: '/settings/documents', label: t('documents.settings.title'), permission: 'documents.templates' },
      { to: '/settings/ai', label: t('settings.ai'), permission: 'proposals.manage' },
      { to: '/settings/site', label: t('settings.siteConfig'), permission: 'catalog.manage' },
      { to: '/settings/dadata', label: t('settings.dadata'), permission: 'studio.manage' },
    ],
  },
  {
    label: t('settings.groupFinance'),
    sections: [
      { to: '/settings/pricing', label: t('settings.pricing'), permission: 'pricing.read' },
      { to: '/settings/tax', label: t('settings.tax'), permission: 'pricing.read' },
      { to: '/settings/coefficients', label: t('settings.coefficients'), permission: 'pricing.read' },
      { to: '/settings/finance', label: t('finance.settings.title'), permission: 'finance.manage' },
      { to: '/settings/bank', label: t('finance.bank.title'), permission: 'finance.manage' },
      { to: '/settings/acquiring', label: t('finance.acquiring.title'), permission: 'finance.manage' },
      // ЭДО видит и подписант студии без `documents.templates`: ему входить через Контур и выбирать сертификат.
      { to: '/settings/edo', label: t('edo.title'), permission: 'documents.templates', signer: true },
    ],
  },
  {
    label: t('settings.groupSystem'),
    sections: [{ to: '/settings/mail', label: t('settings.mail'), permission: 'mail.manage' }],
  },
] as const;

/** Смотрящий — подписант студии: спрашиваем реквизиты студии, только если это что-то меняет в меню. */
function useIsStudioSigner(enabled: boolean): boolean {
  const { data: me } = useSuspenseQuery(meQuery);
  const studio = useQuery({ ...orpc.org.studio.get.queryOptions(), enabled, staleTime: 60_000 });
  return enabled && studio.data?.signerUserId === me.id;
}

const linkClass =
  'relative isolate flex h-8 items-center rounded-md px-2.5 text-small font-semibold text-muted transition-colors hover:bg-surface-2 hover:text-text';

function SettingsLayout() {
  const { can } = usePermissions();
  const isSigner = useIsStudioSigner(!can('documents.templates'));
  const groups = GROUPS.map((group) => ({
    ...group,
    sections: group.sections.filter(
      (s) => !('permission' in s) || can(s.permission) || ('signer' in s && isSigner),
    ),
  })).filter((group) => group.sections.length > 0);
  const count = groups.reduce((n, group) => n + group.sections.length, 0);
  // Выбранный раздел — плашка (колонка) или черта (лента), которая переезжает к новому разделу.
  const pill = useId();
  const underline = useId();
  // Раздел сменился — его содержимое проявляется (первое открытие настроек проявляет оболочка).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = groups.flatMap((group) => group.sections).find((section) => section.to === pathname);
  useCrumbs(current ? [{ label: current.label }] : null);
  return (
    <div className="md:flex md:gap-5 md:px-6 md:pt-5">
      {count > 1 ? (
        <>
          {/* Мобильные: горизонтальная лента, подчёркивание активной ложится на разделитель. */}
          <nav
            aria-label={t('settings.sections')}
            className="mx-4 mt-3 flex gap-4 overflow-x-auto shadow-[inset_0_-1.5px_0_0_var(--g-border-soft)] md:hidden"
          >
            {groups.flatMap((group) =>
              group.sections.map((section) => (
                <Link
                  key={section.to}
                  to={section.to}
                  className="relative pb-1.5 font-bold whitespace-nowrap text-muted transition-colors hover:text-text"
                  activeProps={{ className: 'text-text' }}
                  activeOptions={{ exact: true }}
                >
                  {({ isActive }) => (
                    <>
                      {section.label}
                      {isActive ? (
                        <motion.span
                          aria-hidden="true"
                          layoutId={underline}
                          transition={TRANSITION.layout}
                          style={{ borderRadius: 9999 }}
                          className="absolute inset-x-0 bottom-0 h-0.5 bg-accent"
                        />
                      ) : null}
                    </>
                  )}
                </Link>
              )),
            )}
          </nav>
          {/* Десктоп: колонка разделов по группам. */}
          <nav aria-label={t('settings.sections')} className="hidden w-44 shrink-0 flex-col gap-4 md:flex">
            {groups.map((group) => (
              <div key={group.label}>
                <div className="px-2.5 pb-1 text-caption font-semibold text-muted uppercase">
                  {group.label}
                </div>
                {group.sections.map((section) => (
                  <Link
                    key={section.to}
                    to={section.to}
                    className={linkClass}
                    activeProps={{ className: 'text-accent-text' }}
                    activeOptions={{ exact: true }}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive ? (
                          <motion.span
                            aria-hidden="true"
                            layoutId={pill}
                            transition={TRANSITION.layout}
                            style={{ borderRadius: 8 }}
                            className="absolute inset-0 -z-10 bg-selected"
                          />
                        ) : null}
                        {section.label}
                      </>
                    )}
                  </Link>
                ))}
              </div>
            ))}
          </nav>
        </>
      ) : null}
      <FadeOnChange watch={pathname} className="min-w-0 flex-1">
        <Outlet />
      </FadeOnChange>
    </div>
  );
}
